// Shared rendering for the Productivity day band charts — used by the Users
// tab "Day timeline" view and the person-detail dialog day drill-down.

export const CLS_COLOR = {
  productive: "#10b981",
  unproductive: "#f43f5e",
  neutral: "#94a3b8",
  unclassified: "#cbd5e1",
  idle: "#e2e8f0",
};
export const CLS_LABEL = {
  productive: "Productive", unproductive: "Unproductive",
  neutral: "Neutral", unclassified: "Unclassified", idle: "Idle",
};
export const DETAILS = [
  { id: "raw", label: "RAW", min: 0 },
  { id: "5", label: "5 MIN", min: 5 },
  { id: "15", label: "15 MIN", min: 15 },
  { id: "30", label: "30 MIN", min: 30 },
  { id: "60", label: "60 MIN", min: 60 },
];

const pad = (n) => String(n).padStart(2, "0");
export const clock = (secs) => {
  const h = Math.floor(secs / 3600) % 24;
  const m = Math.floor((secs % 3600) / 60);
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${ap}`;
};
export const axisHour = (h) => {
  const hh = ((h % 24) + 24) % 24;
  const ap = hh < 12 ? "AM" : "PM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12} ${ap}`;
};
export const dur = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h) return `${h}h ${pad(m)}m`;
  if (m) return `${m}m ${pad(s)}s`;
  return `${s}s`;
};

// Coarser detail levels bucket the day into fixed N-minute cells and colour
// each by the dominant class; genuinely empty stretches stay blank. RAW shows
// the real coalesced bands.
export function bucketize(bands, bucketMin, showIdle) {
  const kept = showIdle ? bands : bands.filter((b) => b.c !== "idle");
  if (!bucketMin) return kept;
  const W = bucketMin * 60;
  const buckets = new Map();
  for (const b of kept) {
    let s = b.s;
    while (s < b.e) {
      const idx = Math.floor(s / W);
      const end = Math.min(b.e, (idx + 1) * W);
      const d = end - s;
      let bk = buckets.get(idx);
      if (!bk) { bk = { cls: {}, names: {} }; buckets.set(idx, bk); }
      bk.cls[b.c] = (bk.cls[b.c] || 0) + d;
      bk.names[b.n] = (bk.names[b.n] || 0) + d;
      s = end;
    }
  }
  const out = [];
  for (const [idx, bk] of [...buckets.entries()].sort((a, b) => a[0] - b[0])) {
    const c = Object.entries(bk.cls).sort((a, b) => b[1] - a[1])[0][0];
    const n = Object.entries(bk.names).sort((a, b) => b[1] - a[1])[0][0];
    const s = idx * W;
    const e = (idx + 1) * W;
    const last = out[out.length - 1];
    if (last && last.c === c && last.e === s) { last.e = e; }
    else out.push({ s, e, c, n });
  }
  return out;
}

export function BandTrack({ bands, winStart, winEnd, height = "h-6" }) {
  const span = Math.max(1, winEnd - winStart);
  return (
    <div className={`relative ${height} w-full rounded bg-slate-50 dark:bg-slate-800/40`}>
      {bands.map((b, i) => {
        const s = Math.max(b.s, winStart);
        const e = Math.min(b.e, winEnd);
        if (e <= s) return null;
        const left = ((s - winStart) / span) * 100;
        const width = Math.max(0.15, ((e - s) / span) * 100);
        return (
          <div
            key={i}
            title={`${CLS_LABEL[b.c]} · ${b.n}\n${clock(b.s)} – ${clock(b.e)} (${dur(b.e - b.s)})`}
            className="absolute top-0 h-full"
            style={{ left: `${left}%`, width: `${width}%`, backgroundColor: CLS_COLOR[b.c] }}
          />
        );
      })}
    </div>
  );
}

export function TimelineLegend({ showIdle = false }) {
  const items = ["productive", "unproductive", "neutral", "unclassified", ...(showIdle ? ["idle"] : [])];
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
      {items.map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CLS_COLOR[c] }} />
          {CLS_LABEL[c]}
        </span>
      ))}
    </div>
  );
}

// Self-contained band chart with an hour axis, for the detail dialog. `window`
// is [startSec, endSec]; `bands` are the raw coalesced bands.
export function DayBandChart({ bands, window: win, showIdle = false, detailMin = 0, testid = "day-band-chart" }) {
  const [lo, hi] = win && win.length === 2 ? win : [8 * 3600, 17 * 3600];
  const span = Math.max(1, hi - lo);
  const view = bucketize(bands || [], detailMin, showIdle);
  const ticks = [];
  for (let h = Math.ceil(lo / 3600); h <= Math.floor(hi / 3600); h++) ticks.push(h);
  return (
    <div data-testid={testid}>
      <BandTrack bands={view} winStart={lo} winEnd={hi} />
      <div className="relative mt-1 h-3 text-[10px] text-slate-400">
        {ticks.map((h) => (
          <span key={h} className="absolute -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${((h * 3600 - lo) / span) * 100}%` }}>{axisHour(h)}</span>
        ))}
      </div>
    </div>
  );
}
